import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismCard } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import type {
  BillingDetailsValidationErrors,
  BillingFormData,
  BillingFormField,
} from 'frontend-payments/components/payment/modal-edit-billing-details';
import PaymentModalEditBillingDetails from 'frontend-payments/components/payment/modal-edit-billing-details';
import type { Account } from 'frontend-payments/models';
import type { CountryCode } from 'frontend-utils/utils/countries';

import styles from './billing-details.module.css';

// Mapping from API error keys to our typed field names
const API_ERROR_KEY_TO_FIELD: Record<string, BillingFormField> = {
  'account.billing.vat_number': 'vatNumber',
} as const;

interface PaymentBillingDetailsSignature {
  Args: {
    account: Account | null;
    isAccountLoading: boolean;
  };
}

export default class PaymentBillingDetails extends Component<PaymentBillingDetailsSignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare session: Services['session'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isEditBillingDetailsModalOpen = false;
  @tracked validationErrors: BillingDetailsValidationErrors | undefined =
    undefined;

  updateBillingDetailsTask = task(
    async (data: BillingFormData): Promise<void> => {
      if (!this.args.account) {
        this.toast.error(this.intl.t('general.error'), { showIcon: true });
        return;
      }

      try {
        // Clear any previous validation errors
        this.validationErrors = undefined;

        // Update the account's billing data
        this.args.account.billing = {
          ...this.args.account.billing,
          city: data.city,
          country: data.country as CountryCode,
          legalEntityName: data.legalEntityName,
          state: data.state,
          street: data.street,
          vatNumber: data.vatNumber,
          zipCode: data.zipCode,
        };

        // Save the account record
        await this.args.account.save({
          adapterOptions: {
            usePatch: true,
          },
        });

        this.toast.success(this.intl.t('payments.billing_details.updated'), {
          showIcon: true,
        });

        this.usageAnalytics.track('Billing Details Edited', {
          eventTrigger: 'payment-details-modal',
        });
      } catch (error) {
        // Check if this is a validation error (400 status) with field-specific errors
        const validationErrors = this.parseValidationError(error);

        if (validationErrors) {
          this.validationErrors = validationErrors;
        } else {
          // Only report non-validation errors to Sentry and show generic error
          this.errorTracking.captureException(error);
          this.toast.error(this.intl.t('general.error'), { showIcon: true });
        }

        // Rollback changes on error
        if (this.args.account) {
          this.args.account.rollbackAttributes();
        }
      } finally {
        // Only close modal if there are no validation errors
        if (!this.validationErrors) {
          this.closeEditBillingDetailsModal();
        }
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEditBillingDetailsDisabled() {
    return this.args.isAccountLoading || this.session.isStaffUser;
  }

  parseValidationError(error: unknown): BillingDetailsValidationErrors | null {
    // Check for Ember Data validation errors: { errors: [{ status: '400', detail: '{"field": ["error"]}' }] }
    try {
      const errorObj = error as {
        errors?: { detail?: string; status?: string }[];
      };
      const firstError = errorObj.errors?.[0];

      if (
        firstError?.status === '400' &&
        typeof firstError.detail === 'string'
      ) {
        const rawErrors = JSON.parse(firstError.detail) as Record<
          string,
          string[]
        >;

        // Map API error keys to our typed field names
        const mappedErrors: BillingDetailsValidationErrors = {};

        Object.entries(API_ERROR_KEY_TO_FIELD).forEach(
          ([apiKey, fieldName]) => {
            if (rawErrors[apiKey]) {
              mappedErrors[fieldName] = rawErrors[apiKey];
            }
          },
        );

        return Object.keys(mappedErrors).length > 0 ? mappedErrors : null;
      }
    } catch {
      // Not a validation error
    }

    return null;
  }

  @action
  closeEditBillingDetailsModal(): void {
    this.isEditBillingDetailsModalOpen = false;
    this.validationErrors = undefined;
  }

  @action
  openEditBillingDetailsModal(): void {
    this.isEditBillingDetailsModalOpen = true;
  }

  @action
  async updateBillingDetails(data: BillingFormData): Promise<void> {
    await this.updateBillingDetailsTask.perform(data);
  }

  <template>
    <PrismCard>
      <:header as |Header|>
        <div class="flex items-center justify-between">
          <Header.title>
            <h4 class="text-emphasis">
              {{t "payments.billing_details.title"}}
            </h4>
          </Header.title>
          <PrismButton
            @buttonType="ghost"
            @iconBefore="edit-pencil"
            @isDisabled={{this.isEditBillingDetailsDisabled}}
            @onClick={{this.openEditBillingDetailsModal}}
          />
        </div>
      </:header>
      <:content>
        {{#if @isAccountLoading}}
          <div class={{concat styles.loading-pulse " " "w-1/3"}} />
          <div class={{concat styles.loading-pulse " " "h-[18px] w-1/2"}} />
        {{else}}
          <div class="flex gap-100">
            <h4>
              {{@account.billing.legalEntityName}}
            </h4>
            <p class="text-300--regular">
              ({{t
                "payments.billing_details.account_number"
                accountNr=@account.accountNumber
              }})
            </p>
          </div>
          <p class="text-300--regular">
            {{@account.formattedDetailsText}}
          </p>
        {{/if}}
      </:content>
    </PrismCard>

    {{#if (and this.isEditBillingDetailsModalOpen @account)}}
      <PaymentModalEditBillingDetails
        {{! @glint-expect-error: Incorrect type }}
        @account={{@account}}
        @isUpdating={{this.updateBillingDetailsTask.isRunning}}
        @onCloseModal={{this.closeEditBillingDetailsModal}}
        @onUpdateBillingDetails={{this.updateBillingDetails}}
        @validationErrors={{this.validationErrors}}
      />
    {{/if}}
  </template>
}
